import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Upload, X, Save, ChevronsUpDown, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { QRCodeSVG } from "qrcode.react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SPDEditOrder = () => {
  const navigate = useNavigate();
  const { orderId } = useParams();
  const fileInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(true);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Product specifications for dropdown
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [openProductDropdown, setOpenProductDropdown] = useState(false);
  
  const [formData, setFormData] = useState({
    item_name: "",
    product_id: "",
    company_name: "",
    purchase_order_number: "",
    total_quantity: "",
    status: "Available",
    scheduled_datetime: "",
    note: "",
    image_drive_url: null
  });

  // Fetch order and products on mount
  useEffect(() => {
    fetchOrder();
    fetchProducts();
  }, [orderId]);

  const fetchOrder = async () => {
    try {
      const response = await fetch(`${API_URL}/api/spd/orders/${orderId}`);
      if (response.ok) {
        const order = await response.json();
        setFormData({
          item_name: order.item_name || "",
          product_id: order.product_id || "",
          company_name: order.company_name || "",
          purchase_order_number: order.purchase_order_number || "",
          total_quantity: order.total_quantity?.toString() || "",
          status: order.status || "Available",
          scheduled_datetime: order.scheduled_datetime || "",
          note: order.note || "",
          image_drive_url: order.image_drive_url || null
        });
      } else {
        toast.error("Failed to load order");
        navigate("/spd/orders");
      }
    } catch (error) {
      console.error("Error fetching order:", error);
      toast.error("Failed to load order");
      navigate("/spd/orders");
    } finally {
      setLoadingOrder(false);
    }
  };

  const fetchProducts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/product-specifications`);
      if (response.ok) {
        const data = await response.json();
        setProducts(data || []);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleProductSelect = (product) => {
    setFormData(prev => ({
      ...prev,
      item_name: product.item_name,
      product_id: product.product_id,
      company_name: product.company_name || ""
    }));
    setOpenProductDropdown(false);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Image size must be less than 5MB");
      return;
    }

    setUploadingImage(true);
    
    try {
      const reader = new FileReader();
      reader.onloadend = async () => {
        const base64Data = reader.result;
        
        const response = await fetch(`${API_URL}/api/spd/upload-image`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            image_base64: base64Data,
            filename: file.name
          })
        });

        if (response.ok) {
          const data = await response.json();
          setFormData(prev => ({
            ...prev,
            image_drive_url: data.drive_url
          }));
          toast.success("Image uploaded to Google Drive");
        } else {
          toast.error("Failed to upload image");
        }
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Failed to upload image");
      setUploadingImage(false);
    }
  };

  const removeImage = () => {
    setFormData(prev => ({
      ...prev,
      image_drive_url: null
    }));
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.item_name.trim()) {
      toast.error("Please select an Item");
      return;
    }
    if (!formData.total_quantity) {
      toast.error("Total Quantity is required");
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        item_name: formData.item_name.trim(),
        product_id: formData.product_id,
        company_name: formData.company_name,
        purchase_order_number: formData.purchase_order_number.trim() || null,
        total_quantity: parseInt(formData.total_quantity),
        status: formData.status,
        scheduled_datetime: formData.scheduled_datetime || null,
        note: formData.note.trim() || null,
        image_drive_url: formData.image_drive_url
      };

      const response = await fetch(`${API_URL}/api/spd/orders/${orderId}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderData)
      });

      if (response.ok) {
        toast.success("Order updated successfully!");
        navigate("/spd/orders");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to update order");
      }
    } catch (error) {
      console.error("Error updating order:", error);
      toast.error("Failed to update order");
    } finally {
      setLoading(false);
    }
  };

  if (loadingOrder) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <Card className="bg-black/80 border-2 border-yellow-500/40 shadow-[0_0_30px_rgba(251,191,36,0.15)]">
        <CardHeader className="border-b border-yellow-500/30">
          <CardTitle className="text-2xl font-black text-yellow-400 uppercase tracking-wider">
            Edit Received Purchase Order
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Row 1: Item Name & Company Name */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-yellow-400 font-semibold">
                  Item Name <span className="text-red-400">*</span>
                </Label>
                <Popover open={openProductDropdown} onOpenChange={setOpenProductDropdown}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      className="w-full justify-between h-12 bg-gray-900/80 border-gray-700 text-white hover:bg-gray-800"
                    >
                      {formData.item_name || "Select Item..."}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[400px] p-0 bg-gray-900 border-gray-700">
                    <Command className="bg-transparent">
                      <CommandInput placeholder="Search items..." className="text-white" />
                      <CommandList>
                        <CommandEmpty className="text-gray-400 py-6 text-center">
                          {loadingProducts ? "Loading..." : "No items found."}
                        </CommandEmpty>
                        <CommandGroup className="max-h-64 overflow-y-auto">
                          {products.map((product) => (
                            <CommandItem
                              key={product.id}
                              value={product.item_name}
                              onSelect={() => handleProductSelect(product)}
                              className="text-white hover:bg-gray-800 cursor-pointer"
                            >
                              <Check className={cn("mr-2 h-4 w-4", formData.item_name === product.item_name ? "opacity-100" : "opacity-0")} />
                              <div className="flex flex-col">
                                <span>{product.item_name}</span>
                                <span className="text-xs text-gray-400">{product.product_id} • {product.company_name}</span>
                              </div>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>
              
              <div className="space-y-2">
                <Label className="text-yellow-400 font-semibold">Company Name</Label>
                <Input
                  value={formData.company_name}
                  readOnly
                  className="bg-gray-900/80 border-gray-700 text-white h-12 cursor-not-allowed opacity-80"
                />
              </div>
            </div>

            {/* Row 2: PO Number & Total Quantity */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-yellow-400 font-semibold">Purchase Order Number</Label>
                <Input
                  value={formData.purchase_order_number}
                  onChange={(e) => handleInputChange("purchase_order_number", e.target.value)}
                  placeholder="Enter PO Number"
                  className="bg-gray-900/80 border-gray-700 text-white focus:border-yellow-500 h-12"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-yellow-400 font-semibold">
                  Total Quantity <span className="text-red-400">*</span>
                </Label>
                <Input
                  type="number"
                  value={formData.total_quantity}
                  onChange={(e) => handleInputChange("total_quantity", e.target.value)}
                  className="bg-gray-900/80 border-gray-700 text-white focus:border-yellow-500 h-12"
                  min="1"
                  required
                />
              </div>
            </div>

            {/* Row 3: Status & Due Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-yellow-400 font-semibold">Status</Label>
                <Select value={formData.status} onValueChange={(value) => handleInputChange("status", value)}>
                  <SelectTrigger className="bg-gray-900/80 border-gray-700 text-white h-12">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-gray-900 border-gray-700">
                    <SelectItem value="Available" className="text-blue-400">Available</SelectItem>
                    <SelectItem value="In Progress" className="text-orange-400">In Progress</SelectItem>
                    <SelectItem value="Completed" className="text-emerald-400">Completed</SelectItem>
                    <SelectItem value="Rejected" className="text-red-400">Rejected</SelectItem>
                    <SelectItem value="Revoked" className="text-gray-400">Revoked</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-yellow-400 font-semibold">Delivery Due Date</Label>
                <Input
                  type="datetime-local"
                  value={formData.scheduled_datetime}
                  onChange={(e) => handleInputChange("scheduled_datetime", e.target.value)}
                  className="bg-gray-900/80 border-gray-700 text-white h-12 [color-scheme:dark]"
                />
              </div>
            </div>

            {/* Note */}
            <div className="space-y-2">
              <Label className="text-yellow-400 font-semibold">Note (Optional)</Label>
              <Textarea
                value={formData.note}
                onChange={(e) => handleInputChange("note", e.target.value)}
                placeholder="Add any important note..."
                className="bg-gray-900/80 border-gray-700 text-white focus:border-yellow-500 min-h-[100px]"
              />
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label className="text-yellow-400 font-semibold">Image</Label>
              
              {formData.image_drive_url ? (
                <div className="bg-gray-900/50 border-2 border-yellow-500/40 rounded-lg p-4">
                  <div className="flex items-start gap-4">
                    <div className="bg-white p-2 rounded-lg flex-shrink-0">
                      <QRCodeSVG value={formData.image_drive_url} size={80} level="M" />
                    </div>
                    <div className="flex-1">
                      <p className="text-green-400 font-semibold text-sm">✓ Image uploaded</p>
                      <a href={formData.image_drive_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-xs hover:underline truncate block">
                        {formData.image_drive_url}
                      </a>
                      <Button type="button" variant="outline" size="sm" onClick={removeImage} className="mt-2 border-red-500/50 text-red-400 hover:bg-red-500 hover:text-white">
                        <X className="h-3 w-3 mr-1" /> Remove
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div 
                  className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer ${uploadingImage ? 'border-yellow-500/50' : 'border-gray-700 hover:border-yellow-500/50'}`}
                  onClick={() => !uploadingImage && fileInputRef.current?.click()}
                >
                  {uploadingImage ? (
                    <Loader2 className="h-6 w-6 text-yellow-400 mx-auto animate-spin" />
                  ) : (
                    <>
                      <Upload className="h-6 w-6 text-gray-500 mx-auto mb-1" />
                      <p className="text-gray-400 text-sm">Click to upload</p>
                    </>
                  )}
                </div>
              )}
              
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleImageUpload} className="hidden" disabled={uploadingImage} />
            </div>

            {/* Submit */}
            <div className="flex justify-end gap-4 pt-4 border-t border-yellow-500/30">
              <Button type="button" variant="outline" onClick={() => navigate("/spd/orders")} className="border-gray-600 text-gray-400">
                Cancel
              </Button>
              <Button type="submit" disabled={loading || uploadingImage} className="bg-gradient-to-r from-yellow-500 to-yellow-600 hover:from-yellow-600 hover:to-yellow-700 text-black font-bold px-8">
                {loading ? "Saving..." : <><Save className="h-4 w-4 mr-2" /> Save Changes</>}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default SPDEditOrder;
